import { Dispatch } from "react";
import { AppState } from "./configureStore";
import { createSlice, PayloadAction, AnyAction } from '@reduxjs/toolkit';
import { ILoadableState } from "../definitions/ILoadableState";
import { client, Path } from "../App";
import { GetCMHistory, GetCMHistoryResponse, CancelFile, UploadFileResponse, Upload } from "../DCR.dtos"
import { push } from "connected-react-router";

export interface IHistoryState extends ILoadableState {
    cmHistoryInfo: GetCMHistoryResponse;
    errorMessage?: string;
    cancelFileIsLoading?: boolean;
    uploadFileIsLoading?: boolean;
    isUploadFileError?: boolean;
}

const initialHistoryState: IHistoryState = {
    cmHistoryInfo: {} as GetCMHistoryResponse,
    isLoading: false,
    isLoaded: false,
    cancelFileIsLoading: false,
    uploadFileIsLoading: false,
    isUploadFileError: false,
}

const cmHistory = (state: IHistoryState) => {
    return {
        ...state,
        isLoading: true,
        isLoaded: false,
        cancelFileIsLoading: false
    }
}

const slice = createSlice({
    name: "history",
    initialState: initialHistoryState,
    reducers: {
        resetState: () => {
            return {
                ...initialHistoryState
            }
        },         
        requestCMHistory: (state: IHistoryState) => {
            return cmHistory(state);            
        },
        receiveCMHistory: (state: IHistoryState, action: PayloadAction<{
            cmHistoryInfoResponse: GetCMHistoryResponse;
        }>) => {
            const cmHistoryInfo = action.payload.cmHistoryInfoResponse;

            return {
                ...state,
                cmHistoryInfo,
                isLoading: false,
                isLoaded: true,
                cancelFileIsLoading: false,
                error: false,
                isUploadFileError: false,
                uploadFileIsLoading: false
            }
        },
        receiveCMHistoryError: (state: IHistoryState, action: PayloadAction<{ message: string }>) => {
            const { message } = action.payload;
            return {
                ...state,
                isLoading: false,
                isLoaded: true,
                error: true,
                errorMessage: message
            }
        },
        requestCancelFile: (state: IHistoryState) => {
            return cmHistory(state);
        },

        requestUploadFile: (state: IHistoryState) => {
            return {
                ...state,
                isLoading: true,
                isLoaded: false,
                uploadFileIsLoading: true
            }
        },

        responseUploadFileError: (state: IHistoryState, action: PayloadAction<{ message: string }>) => {
            const uploadErrorMessage = action.payload.message;

            return {
                ...state,
                isLoading: false,
                isLoaded: true,
                error: false,
                uploadFileIsLoading: false,
                isUploadFileError:true,
                errorMessage: uploadErrorMessage
            }
        },

        requestCloseUploadPopup: (state: IHistoryState) => {
            return {
                ...state,
                isLoading: false,
                isLoaded: true,
                error: false,
                uploadFileIsLoading: false,
                isUploadFileError: false,
                errorMessage: ""
            }
        }      
    }
})

const {
    resetState,
    requestCMHistory,
    receiveCMHistory,
    receiveCMHistoryError,
    requestCancelFile,
    requestUploadFile,
    responseUploadFileError,
    requestCloseUploadPopup
} = slice.actions;

function fetchCMHistory(umcID: string, checkLoading: boolean, backgroundUpdate: boolean) {
    return async (dispatch: Dispatch<AnyAction>, getState: () => AppState) => {
        const cmHistoryState = getState().cmHistory;
        if (checkLoading && (cmHistoryState.isLoading || cmHistoryState.isLoaded)) {
            return;
        }

        dispatch(requestCMHistory());

        try {

            const cmHistoryInfoResponse = await client.post(new GetCMHistory({ umcId: umcID }));

            //Check for errors
            if (cmHistoryInfoResponse.responseStatus){
                throw cmHistoryInfoResponse.responseStatus.message;
            }     

            dispatch(receiveCMHistory({ cmHistoryInfoResponse }));

            if (!backgroundUpdate) {
                dispatch(push(Path.ContributionHistory));
            }
        }
        catch (e) {
            console.log(e);
            dispatch(receiveCMHistoryError({ message: e }))
        }

    }
}

function cancelFile(ccaid: string, umcID: string) {
   
    return async (dispatch: Dispatch<AnyAction>, getState: () => AppState) => {
        const cmHistoryState = getState().cmHistory;
        const { cancelFileIsLoading } = getState().cmHistory;
        if (cmHistoryState.isLoading || cancelFileIsLoading) {
            return;
        }

        dispatch(requestCancelFile());

        try {
            const cmHistoryInfoResponse =
                await client.post(new CancelFile({ umcId: umcID, ccaid: ccaid,isSendEmail: true }));

            //Check for errors
            if (cmHistoryInfoResponse.responseStatus)
                throw cmHistoryInfoResponse.responseStatus.message;

            dispatch(receiveCMHistory({ cmHistoryInfoResponse }));

        }
        catch (e) {
            console.log(e);
            dispatch(receiveCMHistoryError({ message: e }))
        }
    }
}

function uploadFile(file: File, payrollDate: string, umcID: string) {
 
    return async (dispatch: Dispatch<AnyAction>, getState: () => AppState) => {
        const cmHistoryState = getState().cmHistory;
        const { uploadFileIsLoading } = getState().cmHistory;
        if (cmHistoryState.isLoading || uploadFileIsLoading) {
            return;
        }
        try {
            dispatch(requestUploadFile());

            const formData = new FormData();
            formData.append("file", file);
            formData.append("payrollDate", payrollDate);
            formData.append("umcID", umcID);
            

            const uploadFileResponse: UploadFileResponse =
                await client.postBody((new Upload()), formData);

            if (uploadFileResponse !== null && uploadFileResponse.success) {
                const cmHistoryInfoResponse: GetCMHistoryResponse = uploadFileResponse.contributionManagementHistory;
                dispatch(receiveCMHistory({ cmHistoryInfoResponse }));
            }

            if (uploadFileResponse !== null && !uploadFileResponse.success) {
                dispatch(responseUploadFileError({ message: uploadFileResponse.errors[0] }));
            }      
        }
        catch (ex) {
            console.log(ex.message);
            dispatch(responseUploadFileError({ message: ex.message }));
        }
    }
}

function closeUploadPopup(){  
    return async (dispatch: Dispatch<AnyAction>, getState: () => AppState) => {
        const cmHistoryState = getState().cmHistory;
        const { cancelFileIsLoading } = getState().cmHistory;
        if (cmHistoryState.isLoading || cancelFileIsLoading) {
            return;
        }
        try {

            dispatch(requestCloseUploadPopup());
        }
        catch (ex) {
            console.log(ex.message);
            //Just need to send the error message back to upload popup hence using this dispatch. - May need to give an appropriate name.
            dispatch(responseUploadFileError({ message: ex.message }));
        }
    }
}

export const historyReducer = slice.reducer;

export const historyActionCreators = {
    fetchCMHistory,
    cancelFile,
    uploadFile,
    requestCMHistory,
    receiveCMHistory,
    receiveCMHistoryError,
    closeUploadPopup,
    resetState
};